import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, OnInit, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

interface DesiredPositionType {
  ID?: string;
  id?: string;
  name: string;
  description?: string;
}

interface CvSkillItem {
  name: string;
  level: string;
  yearsOfExperience: number | null;
}

interface CvJobHistoryItem {
  employerName: string;
  jobTitle: string;
  location: string;
  startedAt: string;
  endedAt: string;
  isCurrent: boolean;
  responsibilities: string;
}

interface CvProjectItem {
  name: string;
  date: string;
  details: string[];
}

@Component({
  selector: 'app-fill-cv-page',
  imports: [RouterLink],
  templateUrl: './fill-cv-page.html',
  styleUrl: './fill-cv-page.css',
})
export class FillCvPage implements OnInit {
  private http = inject(HttpClient);

  public firstName = signal('');
  public secondName = signal('');
  public thirdName = signal('');
  public phoneNumber = signal('');
  public email = signal('');
  public streetAddress = signal('');
  public title = signal('');
  public city = signal('');
  public country = signal('عێراق');
  public zipcode = signal('');
  public preferredSalaryMin = signal<string>('');
  public preferredSalaryMax = signal<string>('');
  public preferredSalaryCurrency = signal('IQD');
  public headline = signal('');
  public notes = signal('');

  public skills = signal<CvSkillItem[]>([]);
  public jobHistories = signal<CvJobHistoryItem[]>([]);
  public projects = signal<CvProjectItem[]>([]);

  public desiredPositionTypes = signal<DesiredPositionType[]>([]);
  public selectedDesiredPositionIDs = signal<string[]>([]);

  public isLoading = signal(false);
  public isSubmitting = signal(false);
  public errorMessage = signal('');
  public successMessage = signal('');

  ngOnInit(): void {
    this.addSkill();
    this.addJobHistory();
    this.addProject();
    void this.loadDesiredPositionTypes();
  }

  public desiredTypeID(item: DesiredPositionType): string {
    return String(item.ID ?? item.id ?? '');
  }

  public updateField(field: 'firstName' | 'secondName' | 'thirdName' | 'phoneNumber' | 'email' | 'streetAddress' | 'title' | 'city' | 'country' | 'zipcode' | 'preferredSalaryMin' | 'preferredSalaryMax' | 'preferredSalaryCurrency' | 'headline' | 'notes', event: Event): void {
    const value = (event.target as HTMLInputElement | HTMLTextAreaElement).value;
    this[field].set(value);
  }

  public addSkill(): void {
    this.skills.update((items) => [...items, { name: '', level: 'INTERMEDIATE', yearsOfExperience: null }]);
  }

  public removeSkill(index: number): void {
    this.skills.update((items) => items.filter((_, i) => i !== index));
  }

  public updateSkill(index: number, field: keyof CvSkillItem, value: string): void {
    this.skills.update((items) => {
      const next = [...items];
      next[index] = {
        ...next[index],
        [field]: field === 'yearsOfExperience' ? (value === '' ? null : Number(value)) : value,
      };
      return next;
    });
  }

  public addJobHistory(): void {
    this.jobHistories.update((items) => [
      ...items,
      {
        employerName: '',
        jobTitle: '',
        location: '',
        startedAt: '',
        endedAt: '',
        isCurrent: false,
        responsibilities: '',
      },
    ]);
  }

  public removeJobHistory(index: number): void {
    this.jobHistories.update((items) => items.filter((_, i) => i !== index));
  }

  public updateJobHistory(index: number, field: keyof CvJobHistoryItem, value: string | boolean): void {
    this.jobHistories.update((items) => {
      const next = [...items];
      next[index] = { ...next[index], [field]: value };
      if (field === 'isCurrent' && value === true) {
        next[index].endedAt = '';
      }
      return next;
    });
  }

  public addProject(): void {
    this.projects.update((items) => [...items, { name: '', date: '', details: [''] }]);
  }

  public removeProject(index: number): void {
    this.projects.update((items) => items.filter((_, i) => i !== index));
  }

  public updateProjectName(index: number, value: string): void {
    this.projects.update((items) => {
      const next = [...items];
      next[index] = { ...next[index], name: value };
      return next;
    });
  }

  public updateProjectDate(index: number, value: string): void {
    this.projects.update((items) => {
      const next = [...items];
      next[index] = { ...next[index], date: value };
      return next;
    });
  }

  public addProjectDetail(projectIndex: number): void {
    this.projects.update((items) => {
      const next = [...items];
      next[projectIndex] = { ...next[projectIndex], details: [...next[projectIndex].details, ''] };
      return next;
    });
  }

  public removeProjectDetail(projectIndex: number, detailIndex: number): void {
    this.projects.update((items) => {
      const next = [...items];
      const details = next[projectIndex].details.filter((_, i) => i !== detailIndex);
      next[projectIndex] = { ...next[projectIndex], details: details.length ? details : [''] };
      return next;
    });
  }

  public updateProjectDetail(projectIndex: number, detailIndex: number, value: string): void {
    this.projects.update((items) => {
      const next = [...items];
      const details = [...next[projectIndex].details];
      details[detailIndex] = value;
      next[projectIndex] = { ...next[projectIndex], details };
      return next;
    });
  }

  public isDesiredPositionSelected(id: string): boolean {
    return this.selectedDesiredPositionIDs().includes(id);
  }

  public toggleDesiredPosition(id: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.selectedDesiredPositionIDs.update((items) => {
      const set = new Set(items);
      if (checked) set.add(id);
      else set.delete(id);
      return Array.from(set);
    });
  }

  public async submit(event: Event): Promise<void> {
    event.preventDefault();
    if (this.isSubmitting()) return;

    if (!this.firstName().trim() || !this.secondName().trim() || !this.phoneNumber().trim() || !this.title().trim() || !this.city().trim() || !this.country().trim()) {
      this.errorMessage.set('تکایە خانە پێویستەکان پڕ بکەرەوە.');
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-request-id': crypto.randomUUID(),
    });

    const payload = {
      first_name: this.firstName().trim(),
      second_name: this.secondName().trim(),
      third_name: this.thirdName().trim(),
      phone_number: this.phoneNumber().trim(),
      email: this.email().trim(),
      street_address: this.streetAddress().trim(),
      title: this.title().trim(),
      city: this.city().trim(),
      country: this.country().trim(),
      zipcode: this.zipcode().trim(),
      preferred_salary_min: this.preferredSalaryMin() ? Number(this.preferredSalaryMin()) : null,
      preferred_salary_max: this.preferredSalaryMax() ? Number(this.preferredSalaryMax()) : null,
      preferred_salary_currency: this.preferredSalaryCurrency(),
      headline: this.headline().trim(),
      notes: this.notes().trim(),
      desired_position_type_ids: this.selectedDesiredPositionIDs(),
      skills: this.skills()
        .filter((item) => item.name.trim())
        .map((item) => ({
          name: item.name.trim(),
          level: item.level,
          years_of_experience: item.yearsOfExperience,
        })),
      job_histories: this.jobHistories()
        .filter((item) => item.employerName.trim() && item.jobTitle.trim() && item.startedAt)
        .map((item) => ({
          employer_name: item.employerName.trim(),
          job_title: item.jobTitle.trim(),
          location: item.location.trim(),
          started_at: new Date(`${item.startedAt}T00:00:00Z`).toISOString(),
          ended_at: item.isCurrent || !item.endedAt ? null : new Date(`${item.endedAt}T00:00:00Z`).toISOString(),
          is_current: item.isCurrent,
          responsibilities: item.responsibilities.trim(),
        })),
      projects: this.projects()
        .filter((item) => item.name.trim())
        .map((item) => ({
          name: item.name.trim(),
          date: item.date ? new Date(`${item.date}T00:00:00Z`).toISOString() : null,
          details: item.details.map((detail) => detail.trim()).filter(Boolean),
        })),
    };

    try {
      await firstValueFrom(this.http.post(`${environment.APIURL}/api/v1/app/external-cvs/public`, payload, { headers }));
      this.successMessage.set('سیڤییەکەت بە سەرکەوتوویی نێردرا. بەزوویی پەیوەندیت پێوە دەکەین.');
      this.firstName.set('');
      this.secondName.set('');
      this.thirdName.set('');
      this.phoneNumber.set('');
      this.email.set('');
      this.streetAddress.set('');
      this.title.set('');
      this.city.set('');
      this.country.set('عێراق');
      this.zipcode.set('');
      this.preferredSalaryMin.set('');
      this.preferredSalaryMax.set('');
      this.preferredSalaryCurrency.set('IQD');
      this.headline.set('');
      this.notes.set('');
      this.skills.set([]);
      this.jobHistories.set([]);
      this.projects.set([]);
      this.addSkill();
      this.addJobHistory();
      this.addProject();
      this.selectedDesiredPositionIDs.set([]);
    } catch (error: any) {
      const message = error?.error?.messages?.[0] ?? error?.error?.error ?? 'نەتوانرا سیڤی بنێردرێت. تکایە دواتر هەوڵ بدەوە.';
      this.errorMessage.set(String(message));
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private async loadDesiredPositionTypes(): Promise<void> {
    this.isLoading.set(true);
    try {
      const response: any = await firstValueFrom(this.http.get(`${environment.APIURL}/api/v1/app/desired-position-types?active=true`, {
        headers: new HttpHeaders({ 'x-request-id': crypto.randomUUID() }),
      }));
      const data = response?.data ?? response?.Data;
      this.desiredPositionTypes.set(Array.isArray(data) ? data : []);
    } catch {
      this.desiredPositionTypes.set([]);
    } finally {
      this.isLoading.set(false);
    }
  }
}
